// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomDatePickerComponent } from '@way/theme-host/components/custom-date-picker/custom-date-picker.component';

/**
 * Modal component for booking appointments: presents a form to collect customer details, select a service, choose date/time, and submit or cancel the booking.
 * @component
 * @selector app-shared-book-appointment-modal
 * @example
 *   <app-shared-book-appointment-modal
 *     [isVisible]="true"
 *     [serviceOptions]="services"
 *     [isSubmitting]="isLoading"
 *     [successMessage]="successMsg"
 *     [errorMessage]="errorMsg"
 *     (close)="handleClose()"
 *     (schedule)="handleSchedule($event)">
 *   </app-shared-book-appointment-modal>
 *
 * This component manages internal form state (full name, email, phone, service type, license plate, preferred date/time, notes),
 * generates 30-minute time slots, validates required fields, and emits a schedule payload suitable for API consumption
 * (preferredDate converted to ISO YYYY-MM-DD). It listens to document clicks to close the custom time dropdown and
 * resets the form whenever the modal becomes visible.
 *
 * Key methods:
 * - generateTimeOptions(): populates timeOptions with 30-minute intervals for a full day.
 * - formatTime12Hour(hour, minute): formats a 24-hour time into a 12-hour string with AM/PM.
 * - resetForm(): clears the formData and closes the time dropdown.
 * - onClose(): emits the close event.
 * - onSchedule(): prepares schedule payload (converts date to ISO string), emits schedule event, and resets the form.
 * - isFormValid(): returns whether required fields are present for submission.
 * - formatDateForDisplay(date): returns a human-readable short-month date string.
 * - toggleTimeDropdown(), selectTime(time), getDisplayTime(): utilities for time selection UI.
 *
 * Lifecycle hooks and listeners:
 * - ngOnChanges(changes): watches isVisible to reset the form when the modal opens.
 * - @HostListener('document:click', ['$event']): closes the time dropdown when clicking outside the time picker.
 *
 * @input boolean isVisible - Controls modal visibility; when set to true the component resets its form state.
 * @input { value: string; label: string }[] serviceOptions - List of service options used to populate the service selector.
 * @input boolean isSubmitting - Indicates an in-progress submission to disable inputs/show spinner.
 * @input string successMessage - Optional success message to display after scheduling.
 * @input string errorMessage - Optional error message to display if scheduling fails.
 * @output EventEmitter<void> close - Emitted when the user closes the modal without scheduling.
 * @output EventEmitter<any> schedule - Emitted with the scheduling payload when the user submits the form (preferredDate formatted as YYYY-MM-DD).
 */
@Component({
  selector: 'app-shared-book-appointment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomDatePickerComponent],
  templateUrl: './book-appointment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./book-appointment-modal.component.scss'],
})
export class SharedBookAppointmentModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() serviceOptions: { value: string; label: string }[] = [];
  @Input() isSubmitting = false;
  @Input() successMessage = '';
  @Input() errorMessage = '';
  @Output() close = new EventEmitter<void>();
  @Output() schedule = new EventEmitter<any>();

  formData = {
    fullName: '',
    email: '',
    phoneNumber: '',
    serviceType: '',
    licensePlate: '',
    preferredDate: null as Date | null,
    preferredTime: '',
    notes: '',
  };

  timeOptions: string[] = [];
  selectedPackage: any = null;
  isTimeDropdownOpen = false;

  constructor() {
    this.generateTimeOptions();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.time-picker-wrapper')) {
      this.isTimeDropdownOpen = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue) {
      // Reset form when modal opens
      this.resetForm();
    }
  }

  private generateTimeOptions(): void {
    // Generate time slots from 12:00 AM to 11:30 PM in 30-minute intervals
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time12 = this.formatTime12Hour(hour, minute);
        times.push(time12);
      }
    }
    this.timeOptions = times;
  }

  private formatTime12Hour(hour: number, minute: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const minuteStr = minute.toString().padStart(2, '0');
    return `${hour12}:${minuteStr} ${period}`;
  }

  /**
   * Reset the appointment booking form to its default empty values and close the time dropdown.
   * @example
   * esetForm()
   * undefined
   * @returns {{void}} No return value.
   **/
  resetForm(): void {
    this.formData = {
      fullName: '',
      email: '',
      phoneNumber: '',
      serviceType: '',
      licensePlate: '',
      preferredDate: null,
      preferredTime: '',
      notes: '',
    };
    this.isTimeDropdownOpen = false;
  }

  onClose(): void {
    this.close.emit();
  }

  onSchedule(): void {
    // Convert Date to ISO string for API compatibility
    const scheduleData = {
      ...this.formData,
      preferredDate: this.formData.preferredDate
        ? this.formData.preferredDate.toISOString().split('T')[0]
        : '',
    };
    this.schedule.emit(scheduleData);
    this.resetForm();
  }

  isFormValid(): boolean {
    return Boolean(
      this.formData.fullName &&
      this.formData.email &&
      this.formData.phoneNumber &&
      this.formData.serviceType &&
      this.formData.licensePlate &&
      this.formData.preferredDate &&
      this.formData.preferredTime
    );
  }

  formatDateForDisplay(date: Date | string | null): string {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString('default', { month: 'short' });
    const year = dateObj.getFullYear();
    return `${day} ${month}, ${year}`;
  }

  toggleTimeDropdown(): void {
    this.isTimeDropdownOpen = !this.isTimeDropdownOpen;
  }

  selectTime(time: string): void {
    this.formData.preferredTime = time;
    this.isTimeDropdownOpen = false;
  }

  getDisplayTime(): string {
    return this.formData.preferredTime || 'Select Time';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
